import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {IUploadDocument} from 'frontier';

export interface IInvoiceTemplate {
  type: string;
  rowid: number;
  signature: string;
  name: string;
}

export interface IInvoiceTemplates {
  data: IInvoiceTemplate[];
}

export interface ITemplateConfigurationDto {
  period?: number;
  charge?: number;
  defaultinterest?: number;
  account?: string;
  costlocation?: string;
  header: THeaderDto;
  letterbox_sender: {
    name: string;
    street: string;
    street_number: string;
    postal_code: string;
    city: string;
  };
  contact_block1?: TContactBlockDto;
  contact_note?: string;
  contact_block2?: TContactBlockDto;
  patient_portal?: {
    url: string;
    colors: TColorOptions;
  };
  bank_info: {
    show_qr_code: boolean;
    name: string;
    iban: string;
    bic: string;
  };
  footer: string[][];
  delay?: number;
  autoarrange?: boolean;
  autocreate?: boolean;
  iscustomdunning?: boolean;
  dunning?: {
    title: string;
    first_block_text: string | string[];
    second_block_title: string;
    second_block_text: string | string[];
    outro: string | string[];
  };
}

export interface ITemplateModificationDto {
  __override: Partial<ITemplateConfigurationDto>

  [key: string]: any;
}

export interface ITemplateModificationDto {
  __override: Partial<ITemplateConfigurationDto>;
  note_below_total?: string;
}

export type TContactBlockDto = {
  title?: string;
  content?: { [key: string]: string };
}

export type TFooterFormValue = string[][];

export type TCustomOptionValue = { key: string; value: string }[];

export interface ITemplateConfigurationFormValue extends Omit<ITemplateConfigurationDto,
  TContactBlock | 'footer' | 'header'> {
  contact_block1: TContactBlockModel;
  contact_block2: TContactBlockModel;
  header: THeaderModel;
  footer: TFooterFormValue;
  custom_options?: TCustomOptionValue;
}

export type TContactBlockModel = {
  title: string;
  content: { label: string; value: string }[];
}

export type TContactBlockForm = FormGroup<{
  title: FormControl<string>;
  content: FormArray<FormGroup<{
    label: FormControl<string>;
    value: FormControl<string>;
  }>>;
}>
export type TFooterForm = FormArray<FormArray<FormControl<string>>>;

export type TCustomOptionForm = FormArray<FormGroup<{ key: FormControl<string>, value: FormControl<string> }>>;

const allFormKeys = [
  'header',
  'title',
  'subtitle',
  'logo',
  'data',
  'width',
  'url',
  'colors',
  'letterbox_sender',
  'bank_info',
  'name',
  'street',
  'street_number',
  'postal_code',
  'city',
  'show_qr_code',
  'iban',
  'bic',
  'contact_block1',
  'content',
  'label',
  'value',
  'contact_note',
  'contact_block2',
  'patient_portal',
  'footer',
  'custom_options',
  'iscustomdunning',
  'dunning',
  'first_block_text',
  'second_block_title',
  'second_block_text',
  'outro',
] as string[];
type exportFormKeysTuple = typeof allFormKeys;
export type TFormKey = exportFormKeysTuple[number];

export function isFormKey(key: string): key is TFormKey {
  return allFormKeys.includes(key as TFormKey);
}

export type THeaderFormGroup = FormGroup<{
  title: FormControl<string>;
  subtitle: FormControl<string>;
  logo?: FormGroup<{
    data: FormControl<IUploadDocument>;
    width: FormControl<string>;
  }>;
}>;

export type TPatientPortalFormGroup = FormGroup<{
  url: FormControl<string>;
  colors: FormControl<TColorOptions>;
}>

export type TLetterBoxSenderFormGroup = FormGroup<{
  name: FormControl<string>;
  street: FormControl<string>;
  street_number: FormControl<string>;
  postal_code: FormControl<string>;
  city: FormControl<string>;
}>

export type TBankInfoFormGroup = FormGroup<{
  show_qr_code: FormControl<boolean>;
  name: FormControl<string>;
  iban: FormControl<string>;
  bic: FormControl<string>;
}>

export type TDunningFormGroup = FormGroup<{
  title: FormControl<string>;
  first_block_text: FormControl<string>;
  second_block_title: FormControl<string>;
  second_block_text: FormControl<string>;
  outro: FormControl<string>;
}>

export interface ITemplateConfigurationForm {
  period?: FormControl<number>;
  charge?: FormControl<number>;
  defaultinterest?: FormControl<number>;
  account?: FormControl<string>;
  costlocation?: FormControl<string>;
  header: THeaderFormGroup;
  letterbox_sender: TLetterBoxSenderFormGroup;
  contact_block1?: TContactBlockForm;
  contact_note?: FormControl<string>;
  contact_block2?: TContactBlockForm;
  patient_portal?: TPatientPortalFormGroup;
  bank_info: TBankInfoFormGroup;
  footer?: TFooterForm;
  custom_options?: TCustomOptionForm;
  delay?: FormControl<number>;
  autoarrange?: FormControl<boolean>;
  autocreate?: FormControl<boolean>;
  iscustomdunning: FormControl<boolean>;
  dunning: TDunningFormGroup;
}

export interface IDataField {
  displayname: string;
  internalname: string;
}

export type TNullableAttributes = 'contact_block1' | 'contact_note' | 'contact_block2' | 'patient_portal';
export type TContactBlock = 'contact_block1' | 'contact_block2';

export type THeaderModel = { title: string, subtitle: string, logo?: TLogoModel };
export type THeaderDto = { title: string, subtitle: string, logo?: TLogoDto };

export type TLogoModel = { data: IUploadDocument, width: string };
export type TLogoDto = { data: string, width: string };

export type TColorOptions = 'default' | 'drk';
